import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import Sidebar from '../components/Sidebar/Sidebar';
import Header from '../components/Header/Header';
import { LOGOUT_IN_PROGRESS_KEY } from '../utils/logout';

const MainLayout: React.FC = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const [isDesktopSidebarCollapsed, setIsDesktopSidebarCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const isSilentRenewInProgress = useRef(false);

  useEffect(() => {
    if (!auth.isLoading && !auth.isAuthenticated) {
      navigate('/');
    }
  }, [auth.isAuthenticated, auth.isLoading, navigate]);

  useEffect(() => {
    const trySilentRenew = async () => {
      if (window.localStorage.getItem(LOGOUT_IN_PROGRESS_KEY) === '1') {
        return;
      }

      if (!auth.isAuthenticated || isSilentRenewInProgress.current) return;

      isSilentRenewInProgress.current = true;
      try {
        await auth.signinSilent();
      } catch (error) {
        console.error('Erro ao renovar token silenciosamente:', error);
      } finally {
        isSilentRenewInProgress.current = false;
      }
    };

    auth.events.addAccessTokenExpiring(trySilentRenew);
    auth.events.addAccessTokenExpired(trySilentRenew);

    return () => {
      auth.events.removeAccessTokenExpiring(trySilentRenew);
      auth.events.removeAccessTokenExpired(trySilentRenew);
    };
  }, [auth]);

  const handleLinkClick = () => {
    if (isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
    }
  };

  if (auth.isLoading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-100">
        <p className="text-lg font-medium text-gray-700">Carregando...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Header onMenuButtonClick={() => setIsMobileMenuOpen(true)} />
      <div className="flex">
        {/* Desktop Sidebar */}
        <div className="hidden md:block">
          <Sidebar 
            isCollapsed={isDesktopSidebarCollapsed}
            setIsCollapsed={setIsDesktopSidebarCollapsed}
            onLinkClick={handleLinkClick}
            isMobile={false}
          />
        </div>

        {/* Mobile Sidebar */}
        <div className={`fixed inset-0 z-40 flex md:hidden transform transition-transform duration-300 ease-in-out ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
          <Sidebar 
            isCollapsed={false}
            setIsCollapsed={() => {}} // Not used in mobile view
            onLinkClick={handleLinkClick}
            isMobile={true}
          />
          <div 
            className={`flex-1 bg-black ${isMobileMenuOpen ? 'opacity-50' : 'opacity-0'}`}
            onClick={() => setIsMobileMenuOpen(false)}
          ></div>
        </div>

        <main className="flex-1 p-4 md:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default MainLayout;
